/**
 * MoveSuccessToast - Feedback visual pós mover arquivos via drag-and-drop
 * 
 * Snackbar com animação, ícone e opção de desfazer.
 * Inspirado no toast do Google Drive após mover itens.
 */

import React from 'react'
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Stack,
  Typography,
  alpha,
  keyframes
} from '@mui/material'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FolderIcon from '@mui/icons-material/Folder'

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`

interface Props {
  open: boolean
  message: string
  folderName?: string
  folderColor?: string
  onClose: () => void
  /** Callback para desfazer a ação */
  onUndo?: () => void
}

export default function MoveSuccessToast({
  open,
  message,
  folderName,
  folderColor = '#1976d2',
  onClose,
  onUndo
}: Props) {
  return (
    <Snackbar
      open={open}
      autoHideDuration={5000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{
        '& .MuiSnackbarContent-root': {
          bgcolor: 'transparent',
          boxShadow: 'none',
          p: 0
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          bgcolor: '#323232',
          color: 'white',
          borderRadius: 2,
          boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
          animation: `${slideUp} 0.3s ease-out`,
          minWidth: 300,
          maxWidth: 500
        }}
      >
        {/* Ícone animado */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: alpha('#4caf50', 0.2),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 20, color: '#4caf50' }} />
        </Box>

        {/* Texto */}
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {message}
          </Typography>
          {folderName && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FolderIcon sx={{ fontSize: 14, color: folderColor }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }} noWrap>
                {folderName}
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Botão de desfazer */}
        {onUndo && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onUndo()
            }}
            sx={{
              color: '#90caf9',
              fontWeight: 600,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                bgcolor: 'rgba(144, 202, 249, 0.1)'
              }
            }}
          >
            Desfazer
          </Button>
        )}
      </Box>
    </Snackbar>
  )
}
